import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Link,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getCourses,
  getCourseRuns,
  getLecturesWithGC,
  setGCLessonToLecture,
  syncLectureWithGCLesson
} from '../services/api';
import { Course, CourseRun } from '../types';

interface LectureGC {
  id: number;
  title: string;
  lesson_id: number | null;
  order: number;
}

export const LecturesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const courseParam = queryParams.get('courseId');
  const runParam = queryParams.get('courseRunId');

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [courses, setCourses] = useState<Course[]>([]);
  const [courseRuns, setCourseRuns] = useState<CourseRun[]>([]);
  const [lectures, setLectures] = useState<LectureGC[]>([]);

  const [gcInputs, setGcInputs] = useState<Record<number, string>>({});
  const [updatingGc, setUpdatingGc] = useState<Record<number, boolean>>({});
  const [syncingLecture, setSyncingLecture] = useState<number | null>(null);

  const [selectedCourse, setSelectedCourse] = useState<number | ''>(courseParam ? Number(courseParam) : '');
  const [selectedRun, setSelectedRun] = useState<number | ''>(runParam ? Number(runParam) : '');

  const updateUrlParams = (params: { courseId?: number | ''; courseRunId?: number | '' }) => {
    const newParams = new URLSearchParams(location.search);
    if (params.courseId !== undefined) {
      if (params.courseId === '') newParams.delete('courseId');
      else newParams.set('courseId', params.courseId.toString());
    }
    if (params.courseRunId !== undefined) {
      if (params.courseRunId === '') newParams.delete('courseRunId');
      else newParams.set('courseRunId', params.courseRunId.toString());
    }
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setCourses(await getCourses());
      } catch {
        setError('Не удалось загрузить курсы');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (selectedCourse !== '') {
      (async () => {
        setLoading(true);
        try {
          setCourseRuns(await getCourseRuns(selectedCourse as number));
        } catch {
          setError('Не удалось загрузить потоки курса');
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setCourseRuns([]);
      setSelectedRun('');
      setLectures([]);
      setGcInputs({});
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedRun !== '') {
      (async () => {
        setLoading(true);
        try {
          const resp = await getLecturesWithGC(selectedRun as number);
          const data = resp.lectures as LectureGC[];
          setLectures(data);
          const inputs: Record<number, string> = {};
          data.forEach(l => { inputs[l.id] = l.lesson_id?.toString() || ''; });
          setGcInputs(inputs);
        } catch {
          setError('Не удалось загрузить лекции');
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLectures([]);
      setGcInputs({});
    }
  }, [selectedRun]);

  const handleCourseChange = (e: SelectChangeEvent) => {
    const id = Number(e.target.value);
    setSelectedCourse(id);
    updateUrlParams({ courseId: id, courseRunId: '' });
  };

  const handleRunChange = (e: SelectChangeEvent) => {
    const id = Number(e.target.value);
    setSelectedRun(id);
    updateUrlParams({ courseRunId: id });
  };

  const handleGcInputChange = (lectureId: number, value: string) => {
    setGcInputs(prev => ({ ...prev, [lectureId]: value }));
  };

  const saveGcLesson = async (lectureId: number) => {
    setUpdatingGc(prev => ({ ...prev, [lectureId]: true }));
    try {
      const resp = await setGCLessonToLecture(lectureId, Number(gcInputs[lectureId] || 0));
      const id = resp.gc_lesson_id as number;
      setLectures(prev => prev.map(l => l.id === lectureId ? { ...l, lesson_id: id } : l));
      setGcInputs(prev => ({ ...prev, [lectureId]: id.toString() }));
    } catch {
      setError(`Ошибка сохранения для лекции ${lectureId}`);
    } finally {
      setUpdatingGc(prev => ({ ...prev, [lectureId]: false }));
    }
  };

  const syncGcLecture = async (lectureId: number) => {
    setSyncingLecture(lectureId);
    try {
      await syncLectureWithGCLesson(lectureId, Number(gcInputs[lectureId] || 0));
    } catch {
      setError(`Ошибка синхронизации для лекции ${lectureId}`);
    } finally {
      setSyncingLecture(null);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Stack direction="row" spacing={4} alignItems="center" sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="course-label">Курс</InputLabel>
          <Select labelId="course-label" value={selectedCourse.toString()} label="Курс" onChange={handleCourseChange} disabled={loading || !courses.length}>
            {courses.map(c => <MenuItem key={c.id} value={c.id.toString()}>{c.title}</MenuItem>)}
          </Select>
          <FormHelperText>Выберите курс</FormHelperText>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }} disabled={selectedCourse === '' || loading}>
          <InputLabel id="run-label">Поток курса</InputLabel>
          <Select labelId="run-label" value={selectedRun.toString()} label="Поток курса" onChange={handleRunChange} disabled={loading || !courseRuns.length}>
            {courseRuns.map(r => <MenuItem key={r.id} value={r.id.toString()}>{r.title}</MenuItem>)}
          </Select>
          <FormHelperText>Выберите поток курса</FormHelperText>
        </FormControl>
        {loading && <CircularProgress size={24} />}
      </Stack>

      {lectures.length > 0 && (
        <>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Урок в GC</TableCell>
                  <TableCell>Ссылки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lectures.map(lec => (
                  <TableRow key={lec.id}>
                    <TableCell>{lec.order}</TableCell>
                    <TableCell>{lec.title}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField size="small" value={gcInputs[lec.id] || ''} onChange={e => handleGcInputChange(lec.id, e.target.value)} disabled={updatingGc[lec.id]} sx={{ width: 140 }} />
                        <Tooltip title="Сохранить">
                          <span>
                            <IconButton onClick={() => saveGcLesson(lec.id)} disabled={updatingGc[lec.id]}>
                              {updatingGc[lec.id] ? <CircularProgress size={20} /> : <SaveIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={gcInputs[lec.id] ? "Синхронизировать" : "Укажите GC-урок"}>
                          <span>
                            <IconButton onClick={() => syncGcLecture(lec.id)} disabled={syncingLecture === lec.id || !gcInputs[lec.id]}>
                              {syncingLecture === lec.id ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Link href={`/notebooks?courseId=${selectedCourse}&courseRunId=${selectedRun}&lectureId=${lec.id}`} underline="hover">Ноутбуки</Link>
                      {' | '}
                      <Link href={`/lectures/${lec.id}/threads`} underline="hover">Проверка</Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Dialog open={syncingLecture !== null}>
            <DialogTitle>Синхронизация лекции</DialogTitle>
            <DialogContent sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <DialogContentText>Выполняется синхронизация лекции</DialogContentText>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};
